// @ts-strict-ignore
import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import { antGrey, runwayColors, tagStyle } from './styles/globalStyles';
import { CompletedReleaseSummary, LifecycleStep, NextReleaseSummary, OrgOverviewStatistic } from '../types';
import { CheckCircleFilled } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Progress from 'antd/lib/progress';
import { allLifecycleEventsForPlatform } from './automations/automationHelperComponents';
import { getReleaseLifecycleStepDisplayInfo, getStabilityInfos } from '../helpers';
import { iconAndAltForIntegrationType } from '../helpers/integrationHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectAppPlatform, selectEffectiveTimezone } from '../selectors/selectors';
import { DatetimeFormat } from '../timeHelpers';

export const SummaryCardLifecycleStep: React.FC<{
    nextReleaseSummary: NextReleaseSummary;
    step: LifecycleStep;
}> = ({ step, nextReleaseSummary }) => {
    const { isComplete, targetDate, displayText, hasDate, displayDate } = getReleaseLifecycleStepDisplayInfo(
        step,
        nextReleaseSummary,
    );

    return (
        <>
            <Space direction="vertical" size={4} style={{ ...INFO_SECTION_STYLE, flex: 1 }}>
                <Space>
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {!isComplete && targetDate.isValid() ? 'Target ' + displayText + ' Date' : displayText}
                    </Text>
                    {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
                </Space>
                <Text
                    style={{
                        color: hasDate ? antGrey.grey8 : antGrey.grey6,
                        fontSize: 18,
                        fontWeight: 600,
                    }}
                >
                    {displayDate}
                </Text>
            </Space>
        </>
    );
};

const INFO_SECTION_STYLE: React.CSSProperties = { justifyContent: 'space-between', minWidth: 60, height: 66 };

export const NextReleaseSummaryCard: React.FC<{
    nextReleaseSummary: NextReleaseSummary;
    appId: string;
}> = ({ nextReleaseSummary, appId }) => {
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const lifecycleSteps = allLifecycleEventsForPlatform(appPlatform);

    return (
        <ReleaseSummaryCard
            version={nextReleaseSummary.displayableVersion}
            tag={
                <Tag color={runwayColors.statusYellow} style={tagStyle}>
                    Next release
                </Tag>
            }
            rightDetail={
                <Progress
                    percent={nextReleaseSummary.percentFeatureComplete}
                    showInfo={false}
                    strokeColor={runwayColors.statusGreen}
                />
            }
        >
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'stretch', gap: 4 }}>
                {lifecycleSteps.map((step, index) => {
                    return (
                        <React.Fragment key={index}>
                            <SummaryCardLifecycleStep step={step} nextReleaseSummary={nextReleaseSummary} />
                            {index !== lifecycleSteps.length - 1 && (
                                <Divider
                                    type="vertical"
                                    dashed
                                    style={{ borderColor: antGrey.grey6, height: '100%' }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </ReleaseSummaryCard>
    );
};

export const LiveReleaseSummaryCard: React.FC<{
    liveReleaseSummary: CompletedReleaseSummary;
    appId: string;
    appStoreRatingStat?: OrgOverviewStatistic;
}> = ({ liveReleaseSummary, appId, appStoreRatingStat }) => {
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    const formattedReleaseDate =
        liveReleaseSummary.releaseDate ?
            dayjs(liveReleaseSummary.releaseDate).tz(effectiveTimezone).format(DatetimeFormat.MonthDate)
        :   '-';

    const stabilityInfosForPrimaryBuild = getStabilityInfos(liveReleaseSummary.appStoreBuilds?.[0]);

    return (
        <ReleaseSummaryCard
            version={liveReleaseSummary.displayableVersion}
            tag={
                <Tag color={runwayColors.statusGreen} style={tagStyle}>
                    Live
                </Tag>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                    <Text style={{ color: antGrey.grey7 }}>Released</Text>
                    <Text
                        style={{
                            color: antGrey.grey8,
                            fontSize: 18,
                            fontWeight: 600,
                        }}
                    >
                        {formattedReleaseDate}
                    </Text>
                </Space>
                <Space style={{ alignItems: 'stretch' }} size={12}>
                    {stabilityInfosForPrimaryBuild.length > 0 ?
                        stabilityInfosForPrimaryBuild.map((info, index) => {
                            const [icon, alt] = iconAndAltForIntegrationType(info.integrationId);
                            const adoptionRate = info.sessionAdoption?.toFixed(0);
                            return (
                                <React.Fragment key={`${info.integrationId}-${info.buildIdentifier ?? index}`}>
                                    <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                                        <Space size={4}>
                                            {icon && <img src={icon} alt={alt} width={14} height={14} />}
                                            <Text style={{ color: antGrey.grey7 }}>Adoption</Text>
                                        </Space>
                                        <Text
                                            style={{
                                                fontSize: '24px',
                                                fontWeight: 600,
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            {adoptionRate ? `${adoptionRate}%` : '-'}
                                        </Text>
                                    </Space>
                                    <Divider
                                        type="vertical"
                                        dashed
                                        style={{ borderColor: antGrey.grey6, height: '100%' }}
                                    />
                                </React.Fragment>
                            );
                        })
                    :   <>
                            <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                                <Text style={{ color: antGrey.grey7 }}>Adoption</Text>
                                <Text
                                    style={{
                                        fontSize: '24px',
                                        fontWeight: 600,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    -
                                </Text>
                            </Space>
                            <Divider type="vertical" dashed style={{ borderColor: antGrey.grey6, height: '100%' }} />
                        </>
                    }
                    {stabilityInfosForPrimaryBuild.length > 0 ?
                        stabilityInfosForPrimaryBuild.map((info, index) => {
                            const [icon, alt] = iconAndAltForIntegrationType(info.integrationId);
                            const sessionStability = info.sessionStability?.toFixed(3);
                            return (
                                <React.Fragment
                                    key={`stability-${info.integrationId}-${info.buildIdentifier ?? index}`}
                                >
                                    <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                                        <Space size={4}>
                                            {icon && <img src={icon} alt={alt} width={14} height={14} />}
                                            <Text style={{ color: antGrey.grey7 }}>Stability</Text>
                                        </Space>
                                        <Text style={{ color: antGrey.grey8, fontSize: '24px', fontWeight: 600 }}>
                                            {sessionStability ? `${sessionStability}%` : '-'}
                                        </Text>
                                    </Space>
                                    <Divider
                                        type="vertical"
                                        dashed
                                        style={{ borderColor: antGrey.grey6, height: '100%' }}
                                    />
                                </React.Fragment>
                            );
                        })
                    :   <>
                            <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                                <Text style={{ color: antGrey.grey7 }}>Stability</Text>
                                <Text style={{ color: antGrey.grey8, fontSize: '24px', fontWeight: 600 }}>-</Text>
                            </Space>
                            <Divider type="vertical" dashed style={{ borderColor: antGrey.grey6, height: '100%' }} />
                        </>
                    }
                    <Space direction="vertical" size={4} style={INFO_SECTION_STYLE}>
                        <Text style={{ color: antGrey.grey7 }}>Ratings</Text>
                        <Text style={{ color: antGrey.grey8, fontSize: '24px', fontWeight: 600 }}>
                            {appStoreRatingStat?.value.toFixed(1)}
                        </Text>
                    </Space>
                </Space>
            </div>
        </ReleaseSummaryCard>
    );
};

interface ReleaseSummaryCardProps {
    tag: React.ReactNode;
    rightDetail?: React.ReactNode;
    version: string;
}

const ReleaseSummaryCard: React.FC<React.PropsWithChildren<ReleaseSummaryCardProps>> = ({
    version,
    children,
    tag,
    rightDetail,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: antGrey.grey4,
                color: antGrey.grey7,
                height: 148,
                minWidth: 600,
                borderRadius: 8,
                padding: 18,
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex' }}>
                    <Space size={18}>
                        <Text style={{ color: antGrey.grey8, fontWeight: 600, fontSize: 18 }}>{version}</Text>
                        {tag}
                    </Space>
                    {rightDetail && (
                        <Space size={16} style={{ flex: 1, justifyContent: 'flex-end' }}>
                            {' '}
                            <Text style={{ ...tagStyle, fontSize: 10, fontWeight: 400, color: antGrey.grey8 }}>
                                feature complete
                            </Text>
                            <div style={{ width: 120 }}>{rightDetail}</div>
                        </Space>
                    )}
                </div>
            </div>
            <Divider dashed style={{ margin: '12px 0px', borderColor: antGrey.grey6 }} />
            {children}
        </div>
    );
};
